import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const contactInfo = [
  {
    icon: MapPin,
    title: "Office Address",
    lines: ["S5, Br Apartments, Chinna Thokatta, Malani Society, Hyderabad - 500011, Telangana, India"],
  },
  {
    icon: Phone,
    title: "Phone",
    lines: ["+91 9110725125"],
    link: "tel:+919110725125",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["info@ultimateinfusions.com"],
    link: "mailto:info@ultimateinfusions.com",
  },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact", partnership_type: "general" });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 lg:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} custom={0} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">
            Get In Touch
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1D3A] leading-tight mb-6">
            Let's Start a Conversation
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base lg:text-lg text-slate-600 leading-relaxed">
            Ready to explore how our beverage solutions can benefit your business?
            Reach out and our team will connect with you promptly.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-2"
          >
            <div className="grid grid-cols-1 gap-8">
              {contactInfo.map((item, i) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#0B1D3A] flex items-center justify-center shrink-0 mt-0.5">
                    <item.icon size={20} className="text-[#C8D616]" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-[#0B1D3A]">{item.title}</h4>
                    {item.lines.map((line, j) => (
                      item.link ? (
                        <a key={j} href={item.link} className="text-base text-slate-600 hover:text-[#2563EB] transition-colors block mt-1">
                          {line}
                        </a>
                      ) : (
                        <p key={j} className="text-base text-slate-600 mt-1">{line}</p>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-3"
          >
            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm">
              {success ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-[#0B1D3A] mb-2">Message Sent!</h4>
                  <p className="text-slate-600">We'll respond within 24 hours.</p>
                  <Button
                    onClick={() => setSuccess(false)}
                    className="mt-6 rounded-full bg-[#0B1D3A] hover:bg-[#1A365D]"
                  >
                    Send Another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label>
                      <Input
                        required placeholder="Your name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="rounded-lg"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</label>
                      <Input
                        required type="email" placeholder="you@company.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="rounded-lg"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone *</label>
                      <Input
                        required placeholder="+91 XXXXX XXXXX"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="rounded-lg"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Company</label>
                      <Input
                        placeholder="Your company name"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        className="rounded-lg"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Message *</label>
                    <Textarea
                      required placeholder="How can we help you?"
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="rounded-lg resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  {error && <p className="text-sm text-red-500" data-testid="contact-error">{error}</p>}
                  <Button
                    type="submit" disabled={loading}
                    className="w-full rounded-full bg-[#C8D616] text-[#0B1D3A] hover:bg-[#B5C314] py-6 text-base font-bold"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
